import { useState } from "react";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { API, useAuth } from "@/App";

const REASONS = [
  { id: "spam", label: "Spam or scam" },
  { id: "harassment", label: "Harassment or bullying" },
  { id: "hate", label: "Hate speech" },
  { id: "nudity", label: "Nudity or sexual content" },
  { id: "violence", label: "Violence or graphic content" },
  { id: "self_harm", label: "Self-harm or dangerous acts" },
  { id: "impersonation", label: "Impersonation" },
  { id: "other", label: "Other" },
];

/**
 * Reusable report-this-content button. Opens a modal that POSTs to /api/report
 * pre-filled with target_type, target_id, and the offending user's id.
 *
 * Required props:
 *   - targetType: "user" | "video" | "post" | "track" | "message" | "live"
 *   - targetId: id of the content (or user id for target_type="user")
 *   - reportedUserId: id of the user who owns the content (defaults to targetId for "user")
 *
 * Optional:
 *   - className, size, variant (passed to <Button>)
 *   - iconOnly (renders just the flag icon)
 *   - label (button label, defaults to "Report")
 */
export function ReportButton({
  targetType,
  targetId,
  reportedUserId,
  className = "",
  size = "sm",
  variant = "outline",
  iconOnly = false,
  label = "Report",
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const offenderId = reportedUserId || (targetType === "user" ? targetId : null);
  const isSelf = user && offenderId === user.user_id;
  if (isSelf) return null;

  return (
    <>
      <Button
        size={size}
        variant={variant}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        className={`border-red-500 text-red-300 hover:bg-red-900 ${className}`}
        data-testid={`report-${targetType}-${targetId}-btn`}
        title="Report"
      >
        <Flag className="w-3 h-3" />
        {!iconOnly && <span className="ml-1">{label}</span>}
      </Button>
      {open && (
        <ReportDialog
          targetType={targetType}
          targetId={targetId}
          reportedUserId={offenderId || ""}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ReportDialog({ targetType, targetId, reportedUserId, onClose }) {
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason) {
      toast.error("Pick a reason");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/report`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reported_user_id: reportedUserId,
          target_type: targetType,
          target_id: targetId,
          reason,
          details,
          context_url: typeof window !== "undefined" ? window.location.pathname : null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof data.detail === "string" ? data.detail : "Could not submit report");
        setSubmitting(false);
        return;
      }
      toast.success("Report sent. A moderator will review it.");
      onClose();
    } catch (e) {
      toast.error("Network error");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="report-dialog"
    >
      <div
        className="bg-[#1a0030] border-4 border-red-500 rounded-lg max-w-md w-full p-6 shadow-[8px_8px_0_#ff0040]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <Flag className="text-red-400" />
          <h2 className="text-2xl font-black">Report this {targetType}</h2>
        </div>
        <p className="text-xs text-pink-200 mb-4">
          We review every report. Don't use this for personal disputes.
        </p>

        <Label className="text-[10px] uppercase font-bold text-pink-300">Reason</Label>
        <div className="grid grid-cols-2 gap-2 mt-1 mb-4">
          {REASONS.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setReason(r.id)}
              className={`text-xs font-bold px-3 py-2 rounded border-2 border-black text-left ${
                reason === r.id ? "bg-red-500 text-white" : "bg-white/5"
              }`}
              data-testid={`reason-${r.id}`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <Label className="text-[10px] uppercase font-bold text-pink-300">Details (optional)</Label>
        <Textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Anything else we should know?"
          className="bg-black border-2 border-pink-500/50 mt-1 mb-4"
          maxLength={500}
          data-testid="report-details"
        />

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} className="border-2 border-white" data-testid="report-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-red-600 hover:bg-red-500 text-white font-black border-2 border-black"
            data-testid="report-submit"
          >
            {submitting ? "Sending…" : "Submit Report"}
          </Button>
        </div>
      </div>
    </div>
  );
}
